<template>
	<Teleport to="body">
		<div
			v-if="openState"
			class="preset-task-manager-overlay"
			@pointerdown.stop
			@click.self="cancel"
		>
			<section
				ref="dialogRef"
				class="preset-task-manager"
				role="dialog"
				aria-modal="true"
				:aria-labelledby="titleId"
				:aria-describedby="[description && descriptionId, showHelp && helpId].filter(Boolean).join(' ') || undefined"
				tabindex="-1"
				@keydown.esc="handleEscape"
				@keydown.tab="trapFocus"
			>
				<header class="preset-task-manager__header">
					<div>
						<h3 :id="titleId">{{ title }}</h3>
						<p v-if="description" :id="descriptionId">{{ description }}</p>
					</div>
					<button
						type="button"
						class="preset-task-manager__icon-button"
						:disabled="saving"
						:aria-label="closeLabel"
						@click="cancel"
					>
						×
					</button>
				</header>

				<div class="preset-task-manager__body">
					<label class="preset-task-manager__label" :for="textareaId">Task list template</label>
					<textarea
						:id="textareaId"
						ref="textareaRef"
						v-textarea-autosize
						class="preset-task-manager__textarea"
						rows="1"
						:disabled="saving"
						:value="listToBulletedText(draft.tasks)"
						:aria-label="textareaLabel"
						:aria-describedby="showHelp ? helpId : undefined"
						placeholder="• Vacuum all carpeted areas&#10;• Mop hard floor areas"
						@input="updateBulletedStringList(draft, 'tasks', $event.target.value, $event.target)"
						@keydown.enter="handleBulletedTextareaEnter($event, draft, 'tasks')"
						@keydown.backspace="handleBulletedTextareaBackspace($event, draft, 'tasks')"
						@keydown.delete="handleBulletedTextareaDelete($event, draft, 'tasks')"
					></textarea>
					<p v-if="showHelp" :id="helpId" class="preset-task-manager__help">
						Press Enter to create a new bullet. Paste one task per line; blank and duplicate lines are removed when saved.
					</p>
					<p v-if="errorMessage" class="preset-task-manager__error" role="alert">
						{{ errorMessage }}
					</p>
				</div>

				<footer class="preset-task-manager__footer">
					<div class="preset-task-manager__actions">
						<button
							type="button"
							class="preset-task-manager__button"
							:disabled="saving"
							@click="cancel"
						>
							Cancel
						</button>
						<button
							type="button"
							class="preset-task-manager__button preset-task-manager__button--primary"
							:disabled="saving"
							@click="save"
						>
							{{ saving ? 'Saving…' : 'Save catalog' }}
						</button>
					</div>
				</footer>
			</section>
		</div>
	</Teleport>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onUnmounted, reactive, ref } from 'vue';
import { vTextareaAutosize } from '@/directives/textareaAutosize';
import { normalizeAssignmentRegularTasksTemplate } from '@/utils/assignmentSubtitlePresets';
import {
	createBulletedTextareaEditor,
	listToBulletedText,
} from '@/utils/bulletedTextarea';

const props = defineProps({
	options: {
		type: Array,
		default: () => [],
	},
	title: {
		type: String,
		default: 'Manage regular cleaning tasks catalog',
	},
	description: {
		type: String,
		default: 'This complete list is inserted into each new Assignment Confirmation for this project. Existing documents keep their current tasks.',
	},
	showHelp: {
		type: Boolean,
		default: true,
	},
	textareaLabel: {
		type: String,
		default: 'Regular cleaning tasks catalog',
	},
	closeLabel: {
		type: String,
		default: 'Close regular cleaning tasks catalog',
	},
	saveErrorMessage: {
		type: String,
		default: 'Could not save the regular cleaning tasks catalog. Please try again.',
	},
	saveOptions: {
		type: Function,
		default: null,
	},
});

const emit = defineEmits(['open', 'close', 'saved']);

const instanceId = getCurrentInstance()?.uid ?? Math.random().toString(36).slice(2);
const titleId = `preset-task-manager-${instanceId}-title`;
const descriptionId = `preset-task-manager-${instanceId}-description`;
const helpId = `preset-task-manager-${instanceId}-help`;
const textareaId = `preset-task-manager-${instanceId}-textarea`;
const dialogRef = ref(null);
const textareaRef = ref(null);
const openState = ref(false);
const draft = reactive({ tasks: [] });
const saving = ref(false);
const errorMessage = ref('');
let returnFocusElement = null;
let destroyed = false;

const normalizedTasks = computed(() => normalizeAssignmentRegularTasksTemplate(draft.tasks));
const {
	handleBulletedTextareaBackspace,
	handleBulletedTextareaDelete,
	handleBulletedTextareaEnter,
	updateBulletedStringList,
} = createBulletedTextareaEditor();

const modalEnvironment = {
	bodyOverflow: null,
	appRoot: null,
	appRootWasInert: false,
};

const lockEnvironment = () => {
	if (
		destroyed ||
		!openState.value ||
		typeof document === 'undefined' ||
		modalEnvironment.bodyOverflow !== null
	) return;
	modalEnvironment.bodyOverflow = document.body.style.overflow;
	document.body.style.overflow = 'hidden';
	const appRoot = document.getElementById('app');
	if (appRoot && !appRoot.contains(dialogRef.value)) {
		modalEnvironment.appRoot = appRoot;
		modalEnvironment.appRootWasInert = Boolean(appRoot.inert);
		appRoot.inert = true;
	}
};

const unlockEnvironment = () => {
	if (typeof document === 'undefined' || modalEnvironment.bodyOverflow === null) return;
	document.body.style.overflow = modalEnvironment.bodyOverflow;
	modalEnvironment.bodyOverflow = null;
	if (modalEnvironment.appRoot) {
		modalEnvironment.appRoot.inert = modalEnvironment.appRootWasInert;
		modalEnvironment.appRoot = null;
		modalEnvironment.appRootWasInert = false;
	}
};

const open = ({ returnFocus = null } = {}) => {
	if (destroyed || openState.value) return;
	returnFocusElement = returnFocus;
	draft.tasks = normalizeAssignmentRegularTasksTemplate(props.options);
	errorMessage.value = '';
	openState.value = true;
	emit('open');
	nextTick(() => {
		if (!openState.value || destroyed) return;
		lockEnvironment();
		(textareaRef.value || dialogRef.value)?.focus?.();
	});
};

const close = ({ restoreFocus = true } = {}) => {
	if (saving.value || !openState.value) return;
	openState.value = false;
	errorMessage.value = '';
	unlockEnvironment();
	emit('close');
	const focusTarget = returnFocusElement;
	returnFocusElement = null;
	if (restoreFocus) {
		nextTick(() => {
			if (!destroyed) focusTarget?.focus?.({ preventScroll: true });
		});
	}
};

const cancel = () => close();

const handleEscape = (event) => {
	if (event?.isComposing) return;
	event?.preventDefault?.();
	event?.stopPropagation?.();
	cancel();
};

const save = async () => {
	if (saving.value) return;
	saving.value = true;
	errorMessage.value = '';
	const tasks = normalizedTasks.value;
	try {
		if (typeof props.saveOptions === 'function') await props.saveOptions(tasks);
		emit('saved', tasks);
		saving.value = false;
		close();
	} catch (error) {
		saving.value = false;
		errorMessage.value = error?.message || props.saveErrorMessage;
	}
};

const trapFocus = (event) => {
	const focusable = Array.from(
		dialogRef.value?.querySelectorAll(
			'button:not(:disabled), textarea:not(:disabled), [href], [tabindex]:not([tabindex="-1"])',
		) || [],
	);
	if (!focusable.length) {
		event.preventDefault();
		dialogRef.value?.focus?.();
		return;
	}
	const first = focusable[0];
	const last = focusable[focusable.length - 1];
	if (document.activeElement === dialogRef.value) {
		event.preventDefault();
		(event.shiftKey ? last : first).focus?.();
	} else if (event.shiftKey && document.activeElement === first) {
		event.preventDefault();
		last.focus?.();
	} else if (!event.shiftKey && document.activeElement === last) {
		event.preventDefault();
		first.focus?.();
	}
};

defineExpose({ open, close });
onUnmounted(() => {
	destroyed = true;
	openState.value = false;
	returnFocusElement = null;
	unlockEnvironment();
});
</script>

<style scoped>
.preset-task-manager-overlay {
	position: fixed;
	z-index: 1400;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 16px;
	background: rgba(15, 23, 42, 0.46);
	box-sizing: border-box;
}

.preset-task-manager {
	display: flex;
	flex-direction: column;
	width: min(680px, calc(100vw - 32px));
	max-height: min(760px, calc(100vh - 32px));
	border: 1px solid var(--border-color, #e5e7eb);
	border-radius: 12px;
	outline: none;
	background: var(--noc-background, #fff);
	box-shadow: 0 22px 60px rgba(15, 23, 42, 0.24);
	overflow: hidden;
}

.preset-task-manager__header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 20px;
	padding: 22px 22px 16px;
}

.preset-task-manager__header h3 {
	margin: 0;
	color: var(--text-color, #333);
	font-size: 1.15rem;
	font-weight: 650;
}

.preset-task-manager__header p {
	margin: 5px 0 0;
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.84rem;
	line-height: 1.4;
}

.preset-task-manager__icon-button {
	display: inline-flex;
	flex: 0 0 32px;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	padding: 0;
	border: 0;
	border-radius: 6px;
	background: transparent;
	color: var(--text-color-secondary, #6b7280);
	font: inherit;
	font-size: 1.45rem;
	line-height: 1;
	cursor: pointer;
}

.preset-task-manager__icon-button:hover,
.preset-task-manager__icon-button:focus-visible {
	background: var(--noc-background-secondary, #f5f6f8);
	color: var(--text-color, #333);
	outline: none;
}

.preset-task-manager__body {
	display: flex;
	flex: 1 1 auto;
	flex-direction: column;
	gap: 8px;
	min-height: 0;
	padding: 0 22px 18px;
	overflow-y: auto;
}

.preset-task-manager__label {
	color: var(--text-color, #333);
	font-size: 0.85rem;
	font-weight: 600;
}

.preset-task-manager__textarea {
	width: 100%;
	height: auto;
	min-height: 96px;
	max-height: min(380px, 42vh);
	padding: 12px 14px;
	border: 1px solid var(--border-color, #d1d5db);
	border-radius: 8px;
	outline: none;
	background: var(--noc-background, #fff);
	color: var(--text-color, #333);
	font: inherit;
	font-size: 0.87rem;
	line-height: 1.5;
	overflow-y: auto;
	resize: none;
	box-sizing: border-box;
}

.preset-task-manager__textarea:hover {
	border-color: var(--border-color-active, #9ca3af);
}

.preset-task-manager__textarea:focus {
	border-color: var(--noc-blue, #1261c9);
}

.preset-task-manager__textarea:disabled,
.preset-task-manager__icon-button:disabled {
	opacity: 0.6;
	cursor: wait;
}

.preset-task-manager__help,
.preset-task-manager__error {
	margin: 0;
	font-size: 0.8rem;
	line-height: 1.4;
}

.preset-task-manager__help {
	color: var(--text-color-secondary, #6b7280);
}

.preset-task-manager__error {
	color: #b42318;
}

.preset-task-manager__footer {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 16px;
	padding: 14px 22px;
	border-top: 1px solid var(--border-color, #e5e7eb);
	background: var(--noc-background-secondary, #fafafb);
}

.preset-task-manager__actions {
	display: flex;
	align-items: center;
	gap: 8px;
}

.preset-task-manager__button {
	min-height: 36px;
	padding: 7px 14px;
	border: 1px solid var(--border-color, #d1d5db);
	border-radius: 7px;
	background: var(--noc-background, #fff);
	color: var(--text-color, #333);
	font: inherit;
	font-size: 0.84rem;
	font-weight: 600;
	cursor: pointer;
}

.preset-task-manager__button:hover,
.preset-task-manager__button:focus-visible {
	background: var(--noc-background-secondary, #f5f6f8);
	outline: none;
}

.preset-task-manager__button--primary {
	border-color: var(--primary-color, #1261c9);
	background: var(--primary-color, #1261c9);
	color: #fff;
}

.preset-task-manager__button--primary:hover,
.preset-task-manager__button--primary:focus-visible {
	background: var(--primary-color-hover, #0d4fa7);
}

.preset-task-manager__button:disabled {
	opacity: 0.6;
	cursor: wait;
}

@media (max-width: 600px) {
	.preset-task-manager__footer {
		align-items: stretch;
		flex-direction: column;
	}

	.preset-task-manager__actions,
	.preset-task-manager__button {
		width: 100%;
	}

	.preset-task-manager__button {
		flex: 1 1 0;
	}
}
</style>
